import { DATE_FORMAT } from '@Ravnur-Inc/core/config';
import isSame from '@Ravnur-Inc/nanoutils/isSame';
import some from '@Ravnur-Inc/nanoutils/some';
import { useVuelidate } from '@vuelidate/core';
import moment, { Moment, max } from 'moment';
import { Vue, Options, setup } from 'vue-class-component';
import ChipsInput from '@/components/chips-input/chips-input';
import MediaPermissionEditor, {
  MediaPermission,
} from '@/components/media/media-permission-editor/media-permission-editor';
import { required } from '@/helpers/validations.rules';
import LookupRepository from '@/repositories/lookup-repository';
import useSecurityStore from '@/store/security';
import { ThumbnailInfo } from '@/types/Entity';
import { LookupEntity, LookupEntity$Type } from '@/types/LookupEntity';
import {
  Media$Details,
  Media$Privacy,
  Media$ExternalPage,
  SINGLE_PLAYABLE_MEDIA_TYPES,
  SINGLE_MEDIA_TYPES,
  PLAYABLE_MEDIA_TYPES,
  Media$Type,
} from '@/types/Media';
import { PortalPageType } from '@/types/Auth';
import MediaLinksList from '../media-links-list/media-links-list';
import { Watch } from '@Ravnur-Inc/decorators';
import MediaAiDescrption from '../media-ai-description/media-ai-description';

import './media-info-editor.scss';

const CN = 'media-info-editor';
const FORMAT = 'YYYYMMDD';

class Props {
  origin: Media$Details;
}

@Options({
  validations: {
    title: { required, $autoDirty: true },
  },
})
export default class MediaInfoEditor extends Vue.with(Props) {
  public v$ = setup(() => useVuelidate());
  private security = setup(() => useSecurityStore());

  private title = '';
  private description = '';
  private thumbnail: Nullable<ThumbnailInfo> = null;
  private mediaPrivacy: Media$Privacy = Media$Privacy.PUBLIC;
  private allowedUsers: LookupEntity[] = [];
  private isEnabledDownload = true;
  private isEnabledEmbedding = true;
  private isRetentionPolicy = false;
  private allowExternalPage: Media$ExternalPage = Media$ExternalPage.NONE;
  private publishDate: Nullable<string> = null;
  private expirationDate: Nullable<string> = null;
  private groups: LookupEntity[] = [];
  private hideRelatedMedia = false;
  private allowRating = true;
  private allowComenting = true;

  private get type() {
    return this.origin.type;
  }

  private get owner() {
    return this.origin.owner;
  }

  private get today() {
    return toString(moment());
  }

  private get maxPublishDate() {
    const { expirationDate } = this;
    if (expirationDate) {
      return toString(moment(expirationDate, DATE_FORMAT).subtract(1, 'days'));
    }
    return null;
  }

  private get minExpirationDate() {
    const { publishDate } = this;
    if (publishDate) {
      const m = max([moment(publishDate, DATE_FORMAT).add(1, 'days'), moment().add(1, 'days')]);
      return toString(m);
    }
    return toString(moment().add(1, 'days'));
  }

  get isPublicPortal() {
    const isPublicMedia = this.origin.allowExternalPage === Media$ExternalPage.PUBLIC;

    return isPublicMedia || this.security.isPublicApplication;
  }

  get local(): Partial<Media$Details> {
    const local = {
      title: this.title,
      description: this.description,
      thumbnail: this.thumbnail,
      privacy: this.mediaPrivacy,
      allowedUsers: this.allowedUsers,
      isEnabledDownload: this.isEnabledDownload,
      isEnabledEmbedding: this.isEnabledEmbedding,
      isRetentionPolicy: this.isRetentionPolicy,
      allowExternalPage: this.allowExternalPage,
      publishDate: this.publishDate,
      groups: this.groups,
      hideRelatedMedia: this.hideRelatedMedia,
      allowRating: this.allowRating,
      allowComenting: this.allowComenting,
    };

    if (!this.isRetentionPolicy) {
      const { expirationDate } = this;

      return { ...local, expirationDate };
    }

    return local;
  }

  private get mediaPermission(): MediaPermission {
    const { type, allowExternalPage, allowedUsers, mediaPrivacy } = this;
    return { type, allowExternalPage, allowedUsers, privacy: mediaPrivacy };
  }

  private set mediaPermission(data: MediaPermission) {
    this.mediaPrivacy = data.privacy;
    this.allowExternalPage = data.allowExternalPage;
    this.allowedUsers = data.allowedUsers;
  }

  private get groupsRepository() {
    return new LookupRepository(LookupEntity$Type.GROUPS);
  }

  private get isSinglePlayableMedia() {
    return some(isSame(this.origin.type), SINGLE_PLAYABLE_MEDIA_TYPES);
  }

  private get isSingleMedia() {
    return some(isSame(this.origin.type), SINGLE_MEDIA_TYPES);
  }

  private get isPlayableMedia() {
    return some(isSame(this.origin.type), PLAYABLE_MEDIA_TYPES);
  }

  private get isPublicAllowed() {
    return this.allowExternalPage === Media$ExternalPage.PUBLIC;
  }

  private get isVideo() {
    return this.origin.type === Media$Type.VIDEO;
  }

  private get descriptionSlots() {
    return {
      toolbar: this.renderAIGenerateButton(),
    };
  }

  @Watch('isRetentionPolicy', { immediate: true })
  onRetainPilicyChange() {
    if (this.isRetentionPolicy) {
      this.expirationDate = null;
    } else {
      this.expirationDate = this.origin.expirationDate;
    }
  }

  public refresh(origin: Media$Details) {
    this.title = origin.title;
    this.description = origin.description;
    this.thumbnail = origin.thumbnail;
    this.mediaPrivacy = origin.privacy;
    this.allowedUsers = [...origin.allowedUsers];
    this.isEnabledDownload = origin.isEnabledDownload;
    this.isEnabledEmbedding = origin.isEnabledEmbedding;
    this.isRetentionPolicy = origin.isRetentionPolicy;
    this.allowExternalPage = origin.allowExternalPage;
    this.publishDate = origin.publishDate;
    this.groups = [...origin.groups];
    this.hideRelatedMedia = origin.hideRelatedMedia;
    this.allowRating = origin.allowRating;
    this.allowComenting = origin.allowComenting;

    if (!origin.isRetentionPolicy) {
      this.expirationDate = origin.expirationDate;
    }
  }

  render() {
    return (
      <div class={CN}>
        {this.renderBasicInfo()}
        {this.renderSharingOptions()}
        {this.renderShareLinks()}
        {this.renderPublishingSchedule()}
        {this.renderGroupsSection()}
        {this.renderPortalSettings()}
      </div>
    );
  }

  private renderSharingOptions() {
    return (
      <>
        <l10n class={`${CN}__section-title`} group="media" tkey="section__permissions" />
        <MediaPermissionEditor
          mediaId={this.origin.id}
          originPrivacy={this.origin.privacy}
          owner={this.owner}
          vModel={[this.mediaPermission, 'value']}
        />
      </>
    );
  }

  private renderShareLinks() {
    return (
      <>
        {this.isPublicAllowed && (
          <MediaLinksList
            class={`${CN}__share-link`}
            origin={this.origin}
            pageType={PortalPageType.PUBLIC}
          />
        )}
      </>
    );
  }

  private renderGroupsSection() {
    if (!this.security.isGroupsAvailable) {
      return;
    }

    return (
      <>
        <l10n class={`${CN}__section-title`} group="media" tkey="field__groups" />
        <ChipsInput
          creator={false}
          propName="name"
          repository={this.groupsRepository}
          v-show={this.security.isGroupsAvailable}
          vModel={[this.groups, 'items']}
        />
      </>
    );
  }

  private renderBasicInfo() {
    return (
      <>
        <text-field
          class={`${CN}__field`}
          errors={this.v$.title.$errors}
          label={this.$t('media', 'field__title')}
          maxLength={100}
          vModel={[this.title, 'value']}
        />

        <multiline-field
          isQuill
          autoHeight={true}
          label={this.$t('media', 'field__description')}
          maxLength={1000}
          v-slots={this.descriptionSlots}
          vModel={[this.description, 'value']}
        />
      </>
    );
  }

  private renderPortalSettings() {
    return (
      <div class={`${CN}__portal-settings`}>
        <l10n class={`${CN}__section-title`} group="media" tkey="section__portal-settings" />

        <checkbox
          class={`${CN}__field`}
          label={this.$t('media', 'field__enable-embedding')}
          v-show={this.isPlayableMedia}
          vModel={[this.isEnabledEmbedding, 'value']}
        />

        <checkbox
          class={`${CN}__field`}
          label={this.$t('media', 'field__enable-download')}
          v-show={this.isSinglePlayableMedia}
          vModel={[this.isEnabledDownload, 'value']}
        />

        {!this.isPublicPortal && (
          <>
            <checkbox
              class={`${CN}__field`}
              label={this.$t('media', 'field__allow-commenting')}
              v-show={this.isSingleMedia}
              vModel={[this.allowComenting, 'value']}
            />
            <checkbox
              class={`${CN}__field`}
              label={this.$t('media', 'field__allow-rating')}
              v-show={this.isSingleMedia}
              vModel={[this.allowRating, 'value']}
            />
            <checkbox
              class={`${CN}__field`}
              label={this.$t('media', 'field__hide-related-media')}
              v-show={this.isSingleMedia}
              vModel={[this.hideRelatedMedia, 'value']}
            />
          </>
        )}
      </div>
    );
  }

  private renderPublishingSchedule() {
    if (!this.isSingleMedia) {
      return;
    }

    return (
      <>
        <l10n class={`${CN}__section-title`} group="media" tkey="section__publishing" />

        <div class={`${CN}__publishing`}>
          <datepicker
            clearable={true}
            label={this.$t('media', 'field__publish-date')}
            maxDate={this.maxPublishDate}
            minDate={this.today}
            vModel={[this.publishDate, 'value']}
          />

          <datepicker
            clearable={true}
            disabled={this.isRetentionPolicy || this.security.isExpirationPolicyEnabled}
            label={this.$t('media', 'field__exp-date')}
            minDate={this.minExpirationDate}
            vModel={[this.expirationDate, 'value']}
          />
        </div>

        <checkbox
          class={`${CN}__field`}
          disabled={!this.security.isManageRetentionAllowed}
          label={this.$t('media', 'field__retain')}
          v-show={this.isSingleMedia}
          vModel={[this.isRetentionPolicy, 'value']}
        />
      </>
    );
  }

  private renderAIGenerateButton() {
    if (!this.security.isAIGenerateDescriptionEnabled || !this.isVideo) {
      return;
    }

    return (
      <MediaAiDescrption
        class={`${CN}__ai-description-btn`}
        media={this.origin}
        onDescriptionGenerated={this.handleAiDescription}
      />
    );
  }

  private handleAiDescription(description: string) {
    this.description = description;
  }
}

export function toString(date: Moment | Date, format: string = FORMAT): string {
  date = date instanceof Date ? moment(date) : date;
  return date.format(format);
}
